import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { XIcon } from '../components/icons';
import { Banner, Button, ErrorText, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { PRIVACY_OPTIONS } from '../lib/privacy';
import type { Visibility } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchVideoShares, setVideoShares } from '../queries/videos';

/**
 * Who may watch one recording, on its own page.
 *
 * The scale itself is a menu, because these are five answers to one question
 * rather than five switches; the detail under it says what the chosen answer
 * means in plain words, since "signed in" and "your team" are not the same
 * thing and the difference matters to whoever is about to send the link.
 *
 * "Only people you name" brings its list with it. The list is kept whatever
 * the setting says, so switching away and back does not lose it, and it
 * writes through on every change like the switches below do.
 */
export function PrivacyControl({
  videoId,
  visibility,
  teamName,
  onChange,
  disabled,
}: {
  videoId: string;
  visibility: Visibility;
  /** The team the recording belongs to, when it belongs to one. */
  teamName: string | null;
  onChange: (next: Visibility) => void;
  /** The owner's team has answered this one for them. */
  disabled?: boolean;
}) {
  const chosen = PRIVACY_OPTIONS.find((option) => option.value === visibility) ?? PRIVACY_OPTIONS[0];

  return (
    <div className="border-b border-hairline py-3.5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-body text-chalk">Who can watch</p>
        <select
          value={visibility}
          disabled={disabled}
          aria-label="Who can watch"
          onChange={(event) => onChange(event.target.value as Visibility)}
          className="h-8 max-w-[14rem] rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk disabled:opacity-40"
        >
          {PRIVACY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-0.5 text-caption text-chalk-faint">{chosen.detail(teamName)}</p>

      {visibility === 'team' && !teamName ? (
        <p className="mt-2 text-caption text-caution">
          This recording is not in a team, so nobody but you can open it. Pick another setting, or record it in a team.
        </p>
      ) : null}

      {visibility === 'invited' ? <ShareList videoId={videoId} readOnly={disabled} /> : null}
    </div>
  );
}

/** Addresses in, addresses out. Each change is saved as it is made. */
function ShareList({ videoId, readOnly }: { videoId: string; readOnly?: boolean }) {
  const client = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey: qk.videoShares(videoId), queryFn: () => fetchVideoShares(videoId) });
  const emails = query.data?.emails ?? [];

  const save = useMutation({
    mutationFn: (next: string[]) => setVideoShares(videoId, next),
    onSuccess: (result) => {
      setError(null);
      client.setQueryData(qk.videoShares(videoId), { emails: result.emails, max: result.max });
    },
    onError: (cause) => setError(errorMessage(cause)),
  });

  const add = () => {
    const entry = draft.trim().toLowerCase();
    if (!entry) return;
    if (emails.includes(entry)) {
      setDraft('');
      return;
    }
    save.mutate([...emails, entry], { onSuccess: () => setDraft('') });
  };

  return (
    <div className="mt-3 rounded border border-hairline bg-slate-950/40 p-3">
      {emails.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {emails.map((email) => (
            <li
              key={email}
              className="flex items-center gap-1 rounded-full border border-hairline bg-slate-900 py-0.5 pl-2.5 pr-1 text-caption text-chalk"
            >
              <span className="max-w-[12rem] truncate">{email}</span>
              <button
                type="button"
                aria-label={`Remove ${email}`}
                disabled={readOnly || save.isPending}
                onClick={() => save.mutate(emails.filter((one) => one !== email))}
                className={cx(
                  'flex h-4 w-4 items-center justify-center rounded-full text-chalk-faint',
                  'hover:bg-slate-700 hover:text-chalk disabled:pointer-events-none disabled:opacity-40',
                )}
              >
                <XIcon size={10} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-caption text-chalk-faint">
          Nobody yet, so nobody but you can open it. Add the addresses to share it with.
        </p>
      )}

      <div className="flex gap-2">
        <input
          type="email"
          value={draft}
          disabled={readOnly || save.isPending}
          placeholder="name@company.com"
          aria-label="Add an email address"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add();
            }
          }}
          className="h-8 min-w-0 flex-1 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk placeholder:text-chalk-faint disabled:opacity-40"
        />
        <Button size="sm" busy={save.isPending} disabled={readOnly || !draft.trim()} onClick={add}>
          Add
        </Button>
      </div>
      <p className="mt-2 text-caption text-chalk-faint">
        Each new address gets an email with the link. They watch by signing in to Slate with that address, which is
        free.
      </p>
      {query.isError ? (
        <div className="mt-2">
          <Banner tone="danger">{errorMessage(query.error)}</Banner>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2">
          <ErrorText>{error}</ErrorText>
        </div>
      ) : null}
    </div>
  );
}
